import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Mail, CheckCircle2, Clock, Eye, Briefcase, Zap, MapPin, Pencil, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/Shell";
import { fmtDate, fmtDuration, fmtDuration15 } from "@/lib/format";
import { DepotPicker } from "@/components/DepotPicker";
import { useDepot } from "@/context/DepotContext";
import { usePay } from "@/context/CompanyContext";
import { inCurrentPeriod, periodWord } from "@/lib/payPeriod";
import { InviteCrewDialog } from "@/components/crew/InviteCrewDialog";

import { api } from "@/lib/api";

const qtr = (s) => Math.round((Math.max(0, s || 0) / 3600) * 4) / 4;

export const WorkersTab = () => {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", employment_type: "full_time", home_depot_id: "" });
  const { isAdmin, nameOf } = useDepot();

  const [editWorker, setEditWorker] = useState(null);
  const [editForm, setEditForm] = useState({ email: "", name: "", employment_type: "full_time", home_depot_id: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const [detail, setDetail] = useState(null); // selected worker
  const [detailData, setDetailData] = useState({ jobs: [], shifts: [], loading: true });

  const [quick, setQuick] = useState(null); // worker being quick-assigned
  const [quickJobs, setQuickJobs] = useState({ jobs: [], loading: true });
  const [assigning, setAssigning] = useState("");

  const [onSite, setOnSite] = useState(new Set()); // emails + ids currently clocked in
  const pay = usePay();
  const [weekByWorker, setWeekByWorker] = useState(new Map()); // email/id -> worked secs this pay week

  // Who is clocked in right now (open shift) — refreshed live for the "on site" badge.
  // Also tallies each worker's worked time for the current pay week (Thu -> Wed).
  const refreshOnSite = async () => {
    try {
      const ts = await api.coordinatorTimesheets();
      const s = new Set();
      const week = new Map();
      (ts || []).forEach((x) => {
        if (x.is_open) {
          if (x.user_email) s.add(x.user_email.toLowerCase());
          if (x.user_id) s.add(x.user_id);
        }
        if (inCurrentPeriod(x.clock_in, pay)) {
          const secs = x.worked_seconds || 0;
          if (x.user_email) { const k = x.user_email.toLowerCase(); week.set(k, (week.get(k) || 0) + secs); }
          if (x.user_id) week.set(x.user_id, (week.get(x.user_id) || 0) + secs);
        }
      });
      setOnSite(s);
      setWeekByWorker(week);
    } catch { /* best-effort */ }
  };

  const load = async () => {
    setLoading(true);
    try { setWorkers(await api.workers()); } catch { toast.error("Failed to load workers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); refreshOnSite(); }, []);
  useEffect(() => {
    const t = setInterval(refreshOnSite, 30000); // live-refresh every 30s
    return () => clearInterval(t);
  }, []);

  const openQuick = async (w) => {
    setQuick(w);
    setQuickJobs({ jobs: [], loading: true });
    try {
      const jobs = await api.jobs();
      // Active / unassigned jobs — anything not yet completed can be dispatched.
      const open = jobs.filter((j) => j.status !== "completed");
      setQuickJobs({ jobs: open, loading: false });
    } catch { setQuickJobs({ jobs: [], loading: false }); }
  };

  const assignTo = async (job) => {
    if (!quick) return;
    setAssigning(job.id);
    try {
      await api.updateJob(job.id, { worker_email: quick.email });
      api.notifyAssignment(quick.email, job.name).catch(() => {});
      toast.success(`${quick.name || quick.email} assigned to ${job.name}`);
      setQuickJobs((s) => ({ ...s, jobs: s.jobs.map((j) => j.id === job.id ? { ...j, assigned_email: quick.email, assigned_name: quick.name || quick.email } : j) }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign"); }
    finally { setAssigning(""); }
  };

  const openDetail = async (w) => {
    setDetail(w);
    setDetailData({ jobs: [], shifts: [], loading: true });
    try {
      const [jobs, ts] = await Promise.all([api.jobs(), api.coordinatorTimesheets().catch(() => [])]);
      const em = (w.email || "").toLowerCase();
      const wjobs = jobs.filter((j) => (j.assigned_email || "").toLowerCase() === em);
      const wshifts = ts.filter((s) => (s.user_email || "").toLowerCase() === em || s.user_id === w.id);
      setDetailData({ jobs: wjobs, shifts: wshifts, loading: false });
    } catch { setDetailData({ jobs: [], shifts: [], loading: false }); }
  };

  const add = async () => {
    if (!form.email) { toast.error("Email is required"); return; }
    try {
      await api.addWorker({ ...form, home_depot_id: form.home_depot_id || undefined });
      toast.success("Worker added to roster");
      setOpen(false); setForm({ email: "", name: "", employment_type: "full_time", home_depot_id: "" }); load();
    } catch (e) {
      if (e?.response?.status === 402) setOpen(false);
      toast.error(e?.response?.data?.detail || "Could not add worker");
    }
  };

  const openEdit = (w) => {
    setEditForm({ email: w.email, name: w.name || "", employment_type: w.employment_type || "full_time", home_depot_id: w.home_depot_id || "" });
    setEditWorker(w);
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.updateWorker(editForm);
      toast.success("Worker updated");
      setEditWorker(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update worker"); }
    finally { setSavingEdit(false); }
  };

  const remove = async (email) => {
    try { await api.removeWorker(email); toast.success("Worker removed"); load(); }
    catch { toast.error("Could not remove worker"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div className="fade-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold">Crew Roster</h2>
          <p className="text-sm text-muted-foreground">{workers.length} worker{workers.length !== 1 ? "s" : ""} · tap a card to quick-assign a job</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
        <InviteCrewDialog />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-worker-btn" className="rounded-full bg-primary hover:bg-primary/90 text-white"><Plus className="w-4 h-4 mr-2" /> Add worker</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-display text-xl">Add worker</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Worker email *</Label>
                <Input data-testid="worker-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="worker@email.com" /></div>
              <div className="space-y-2"><Label>Display name</Label>
                <Input data-testid="worker-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Optional" /></div>
              <div className="space-y-2"><Label>Employment type</Label>
                <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                  <SelectTrigger data-testid="worker-employment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full-time</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Casual workers can accept or decline each rostered shift.</p></div>
              <DepotPicker always value={form.home_depot_id} onChange={(v) => setForm({ ...form, home_depot_id: v })} testId="worker-home-depot-select" label="Home depot" noneLabel="No home depot" hint="Shared crew can work for any depot. Shifts count toward their home depot unless they're rostered or on a job elsewhere that day." />
              <p className="text-xs text-muted-foreground">If they haven&apos;t signed up yet, their jobs auto-link when they register with this email.</p>
            </div>
            <DialogFooter><Button data-testid="worker-add-submit" onClick={add} className="rounded-full bg-primary hover:bg-primary/90 text-white">Add worker</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {workers.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-2xl"><p className="text-muted-foreground">No workers yet. Add your first crew member.</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {workers.map((w) => {
            const live = onSite.has((w.email || "").toLowerCase()) || (w.id && onSite.has(w.id));
            const weekSecs = weekByWorker.get((w.email || "").toLowerCase()) || (w.id ? weekByWorker.get(w.id) : 0) || 0;
            return (
            <div key={w.email} data-testid={`worker-card-${w.email}`} className="bg-card border border-border rounded-xl p-5 flex items-center gap-4 transition-colors hover:border-primary/40">
              <button type="button" data-testid={`quick-assign-${w.email}`} onClick={() => openQuick(w)} className="flex items-center gap-4 min-w-0 flex-1 text-left group">
                <span className="relative w-11 h-11 rounded-full bg-secondary flex items-center justify-center font-bold font-mono text-sm shrink-0 group-hover:bg-primary/15 transition-colors">
                  {(w.name || w.email).slice(0, 2).toUpperCase()}
                  {live && <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-card animate-pulse" data-testid={`onsite-dot-${w.email}`} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate flex items-center gap-1.5">{w.name || w.email.split("@")[0]}<Zap className="w-3.5 h-3.5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" /></p>
                  <p className="text-xs text-muted-foreground truncate flex items-center gap-1"><Mail className="w-3 h-3" /> {w.email}</p>
                  <p className="text-xs mt-1 inline-flex items-center gap-1.5 flex-wrap">
                    <span data-testid={`employment-badge-${w.email}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${w.employment_type === "casual" ? "bg-sky-500/15 text-sky-600" : "bg-secondary text-muted-foreground"}`}>
                      {w.employment_type === "casual" ? "Casual" : "Full-time"}
                    </span>
                    {live ? (
                      <span data-testid={`onsite-badge-${w.email}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-600 px-2 py-0.5 font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> On site now
                      </span>
                    ) : w.registered
                      ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> <span className="text-emerald-600">Active</span></span>
                      : <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3 text-amber-500" /> <span className="text-amber-600">Pending signup</span></span>}
                    {isAdmin && w.home_depot_id && nameOf(w.home_depot_id) && <span data-testid={`home-depot-${w.email}`} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 font-medium"><MapPin className="w-3 h-3" /> {nameOf(w.home_depot_id)}</span>}
                    {typeof w.total_jobs === "number" && w.total_jobs > 0 && <span className="text-muted-foreground">· {w.total_jobs} job{w.total_jobs !== 1 ? "s" : ""}</span>}
                    {weekSecs > 0 && <span className="text-muted-foreground" data-testid={`week-hours-${w.email}`}>· {fmtDuration15(weekSecs)} this pay {periodWord(pay)}</span>}
                  </p>
                </div>
              </button>
              <Button variant="ghost" size="icon" data-testid={`edit-worker-${w.email}`} onClick={() => openEdit(w)} className="rounded-full shrink-0"><Pencil className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" data-testid={`view-worker-${w.email}`} onClick={() => openDetail(w)} className="rounded-full shrink-0"><Eye className="w-4 h-4" /></Button>
              {isAdmin && (
                <Button variant="ghost" size="icon" data-testid={`remove-worker-${w.email}`} onClick={() => remove(w.email)} className="text-destructive hover:bg-destructive/10 rounded-full shrink-0">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          );})}
        </div>
      )}

      <Dialog open={!!quick} onOpenChange={(v) => !v && setQuick(null)}>
        <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto sm:max-h-[calc(100dvh-3rem)]" data-testid="quick-assign-dialog">
          {quick && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl flex items-center gap-2"><Zap className="w-5 h-5 text-primary" /> Quick assign — {quick.name || quick.email.split("@")[0]}</DialogTitle>
                <DialogDescription>Tap a job to make {quick.name || "this worker"} the lead. Only active / unassigned jobs are shown.</DialogDescription>
              </DialogHeader>
              {quickJobs.loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              ) : quickJobs.jobs.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No active jobs to assign right now.</p>
              ) : (
                <div className="space-y-2" data-testid="quick-assign-jobs">
                  {quickJobs.jobs.map((j) => {
                    const isCurrent = (j.assigned_email || "").toLowerCase() === (quick.email || "").toLowerCase();
                    return (
                      <button
                        key={j.id}
                        type="button"
                        data-testid={`quick-assign-job-${j.id}`}
                        disabled={isCurrent || assigning === j.id}
                        onClick={() => assignTo(j)}
                        className={`w-full text-left rounded-xl border p-3.5 flex items-center gap-3 transition-all ${isCurrent ? "border-emerald-500/40 bg-emerald-500/5 cursor-default" : "border-border hover:border-primary/50 hover:bg-secondary/50"}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{j.name}</p>
                          <p className="text-xs text-muted-foreground truncate flex items-center gap-1.5">
                            {(j.client || j.address) && <><MapPin className="w-3 h-3" /> {j.client || j.address} · </>}
                            {isCurrent ? "Currently assigned here" : (j.assigned_name || j.assigned_email || "Unassigned")}
                          </p>
                        </div>
                        {assigning === j.id
                          ? <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
                          : isCurrent
                            ? <span className="text-xs text-emerald-600 font-medium inline-flex items-center gap-1 shrink-0"><CheckCircle2 className="w-4 h-4" /> Lead</span>
                            : <StatusBadge status={j.status} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto sm:max-h-[calc(100dvh-3rem)]" data-testid="worker-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{detail.name || detail.email.split("@")[0]}</DialogTitle>
                <DialogDescription>{detail.email} · {detail.registered ? "Active" : "Pending signup"}</DialogDescription>
              </DialogHeader>
              {detailData.loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              ) : (
                <div className="space-y-5">
                  <div className="grid grid-cols-3 gap-3">
                    <Stat label="Jobs" value={detailData.jobs.length} />
                    <Stat label="Shifts" value={detailData.shifts.length} />
                    <Stat label="Total hours" value={`${qtr(detailData.shifts.reduce((a, s) => a + (s.worked_seconds || 0), 0)).toFixed(2)}h`} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> Jobs</p>
                    <div className="space-y-1.5" data-testid="worker-detail-jobs">
                      {detailData.jobs.length === 0 && <p className="text-xs text-muted-foreground">No jobs assigned.</p>}
                      {detailData.jobs.map((j) => (
                        <div key={j.id} className="flex items-center justify-between text-sm bg-secondary/50 rounded-lg px-3 py-2">
                          <span className="truncate">{j.name}</span>
                          <StatusBadge status={j.status} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Recent shifts</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {detailData.shifts.length === 0 && <p className="text-xs text-muted-foreground">No shifts logged.</p>}
                      {detailData.shifts.slice(0, 20).map((s) => (
                        <div key={s.id} className="flex items-center justify-between text-xs bg-secondary/40 rounded px-3 py-1.5">
                          <span>{fmtDate(s.clock_in)}</span>
                          <span className="font-mono">{fmtDuration(s.worked_seconds)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!editWorker} onOpenChange={(v) => !v && setEditWorker(null)}>
        <DialogContent className="max-w-md" data-testid="edit-worker-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><UserCog className="w-5 h-5 text-primary" /> Edit worker</DialogTitle>
            <DialogDescription>{editForm.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Display name</Label>
              <Input data-testid="edit-worker-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Display name" /></div>
            <div className="space-y-2"><Label>Employment type</Label>
              <Select value={editForm.employment_type} onValueChange={(v) => setEditForm({ ...editForm, employment_type: v })}>
                <SelectTrigger data-testid="edit-worker-employment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_time">Full-time</SelectItem>
                  <SelectItem value="casual">Casual</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Casual workers accept or decline each rostered shift.</p></div>
            <DepotPicker always value={editForm.home_depot_id} onChange={(v) => setEditForm({ ...editForm, home_depot_id: v })} testId="edit-worker-home-depot" label="Home depot" noneLabel="No home depot" />
          </div>
          <DialogFooter>
            <Button data-testid="edit-worker-save" onClick={saveEdit} disabled={savingEdit} className="rounded-full bg-primary hover:bg-primary/90 text-white">
              {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-border p-3 text-center">
    <p className="text-lg font-bold font-display">{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);
